import {
  Tooltip,
  TooltipContent,
  TooltipProviderIfMissing,
  TooltipTrigger,
  TooltipVariant,
} from "@/components/ui/tooltip";
import { cva, type VariantProps } from "class-variance-authority";
import * as React from "react";

import { cn } from "@/common/helpers/utils";

export enum BadgeVariant {
  Filled = "filled",
  Outlined = "outlined",
  Ghost = "ghost",
}

export enum BadgeColorVariant {
  Primary = "primary",
  Secondary = "secondary",
  Success = "success",
  Warning = "warning",
  Error = "error",
}

const badgeVariants = cva(cn("inline-flex items-center rounded-md border px-2.5 py-0.5 text-xs transition-colors"), {
  variants: {
    variant: {
      [BadgeVariant.Filled]: "",
      [BadgeVariant.Outlined]: "",
      [BadgeVariant.Ghost]: "border-transparent bg-transparent",
    },
    colorVariant: {
      [BadgeColorVariant.Primary]: "",
      [BadgeColorVariant.Secondary]: "",
      [BadgeColorVariant.Success]: "",
      [BadgeColorVariant.Warning]: "",
      [BadgeColorVariant.Error]: "",
    },
  },
  compoundVariants: [
    {
      variant: BadgeVariant.Filled,
      colorVariant: BadgeColorVariant.Primary,
      class: "border-transparent bg-primary text-primary-foreground data-[clickable]:hover:bg-primary/80",
    },
    {
      variant: BadgeVariant.Filled,
      colorVariant: BadgeColorVariant.Secondary,
      class: "border-transparent bg-secondary text-secondary-foreground data-[clickable]:hover:bg-secondary/80",
    },
    {
      variant: BadgeVariant.Filled,
      colorVariant: BadgeColorVariant.Success,
      class: "border-transparent bg-success text-success-foreground data-[clickable]:hover:bg-success/80",
    },
    {
      variant: BadgeVariant.Filled,
      colorVariant: BadgeColorVariant.Warning,
      class: "border-transparent bg-warning text-warning-foreground data-[clickable]:hover:bg-warning/80",
    },
    {
      variant: BadgeVariant.Filled,
      colorVariant: BadgeColorVariant.Error,
      class: "border-transparent bg-error text-error-foreground data-[clickable]:hover:bg-error/80",
    },
    {
      variant: BadgeVariant.Outlined,
      colorVariant: BadgeColorVariant.Primary,
      class: "border border-primary/50 bg-primary/10 text-primary data-[clickable]:hover:bg-primary/20",
    },
    {
      variant: BadgeVariant.Outlined,
      colorVariant: BadgeColorVariant.Secondary,
      class:
        "border border-muted-foreground/30 bg-muted-foreground/10 text-muted-foreground data-[clickable]:hover:bg-muted-foreground/20",
    },
    {
      variant: BadgeVariant.Outlined,
      colorVariant: BadgeColorVariant.Success,
      class: "border border-success/50 bg-success/10 text-success data-[clickable]:hover:bg-success/20",
    },
    {
      variant: BadgeVariant.Outlined,
      colorVariant: BadgeColorVariant.Warning,
      class: "border border-warning/50 bg-warning/10 text-warning data-[clickable]:hover:bg-warning/20",
    },
    {
      variant: BadgeVariant.Outlined,
      colorVariant: BadgeColorVariant.Error,
      class: "border border-error/50 bg-error/10 text-error data-[clickable]:hover:bg-error/20",
    },
    {
      variant: BadgeVariant.Ghost,
      colorVariant: BadgeColorVariant.Primary,
      class: "text-primary data-[clickable]:hover:bg-primary/20",
    },
    {
      variant: BadgeVariant.Ghost,
      colorVariant: BadgeColorVariant.Secondary,
      class: "text-muted-foreground data-[clickable]:hover:bg-muted-foreground/20",
    },
    {
      variant: BadgeVariant.Ghost,
      colorVariant: BadgeColorVariant.Success,
      class: "text-success data-[clickable]:hover:bg-success/20",
    },
    {
      variant: BadgeVariant.Ghost,
      colorVariant: BadgeColorVariant.Warning,
      class: "text-warning data-[clickable]:hover:bg-warning/20",
    },
    {
      variant: BadgeVariant.Ghost,
      colorVariant: BadgeColorVariant.Error,
      class: "text-error data-[clickable]:hover:bg-error/20",
    },
  ],
  defaultVariants: {
    variant: BadgeVariant.Filled,
    colorVariant: BadgeColorVariant.Primary,
  },
});

function getBadgeTooltipVariant(variant: BadgeVariant, colorVariant: BadgeColorVariant): TooltipVariant {
  if (variant === BadgeVariant.Ghost) return TooltipVariant.Ghost;

  if (variant === BadgeVariant.Outlined) {
    switch (colorVariant) {
      case BadgeColorVariant.Primary:
        return TooltipVariant.OutlinedPrimary;
      case BadgeColorVariant.Secondary:
        return TooltipVariant.Outlined;
      case BadgeColorVariant.Success:
        return TooltipVariant.OutlinedSuccess;
      case BadgeColorVariant.Warning:
        return TooltipVariant.OutlinedWarning;
      case BadgeColorVariant.Error:
        return TooltipVariant.OutlinedError;
    }
  }

  switch (colorVariant) {
    case BadgeColorVariant.Primary:
      return TooltipVariant.Primary;
    case BadgeColorVariant.Secondary:
      return TooltipVariant.Secondary;
    case BadgeColorVariant.Success:
      return TooltipVariant.Success;
    case BadgeColorVariant.Warning:
      return TooltipVariant.Warning;
    case BadgeColorVariant.Error:
      return TooltipVariant.Error;
  }
}

export interface BadgeProps
  extends Omit<React.HTMLAttributes<HTMLDivElement>, "title">, VariantProps<typeof badgeVariants> {
  tooltip?: string | React.ReactNode;
  tooltipContent?: React.ReactNode;
  tooltipVariant?: TooltipVariant;
}

function Badge({ className, variant, colorVariant, tooltip, tooltipContent, tooltipVariant, ...props }: BadgeProps) {
  const badgeElement = (
    <div
      role={props.onClick ? "button" : undefined}
      tabIndex={props.onClick ? 0 : undefined}
      data-clickable={props.onClick ? "" : undefined}
      onKeyDown={(e) => {
        if (props.onClick && (e.key === "Enter" || e.key === " ")) {
          e.preventDefault();
          e.currentTarget.click();
        }
      }}
      className={cn(badgeVariants({ variant, colorVariant }), props.onClick && "cursor-pointer", className)}
      {...props}
    />
  );

  if (tooltip || tooltipContent) {
    const derivedTooltipVariant =
      tooltipVariant ??
      getBadgeTooltipVariant(variant ?? BadgeVariant.Filled, colorVariant ?? BadgeColorVariant.Primary);

    return (
      <TooltipProviderIfMissing>
        <Tooltip>
          <TooltipTrigger asChild>{badgeElement}</TooltipTrigger>
          <TooltipContent variant={derivedTooltipVariant}>{tooltipContent || tooltip}</TooltipContent>
        </Tooltip>
      </TooltipProviderIfMissing>
    );
  }

  return badgeElement;
}

Badge.displayName = "Badge";

export { Badge, badgeVariants };
